import Box from '@mui/material/Box/Box'
import IconButton from '@mui/material/IconButton/IconButton'
import Table from '@mui/material/Table/Table'
import TableBody from '@mui/material/TableBody/TableBody'
import TableCell from '@mui/material/TableCell/TableCell'
import tableCellClasses from '@mui/material/TableCell/tableCellClasses'
import TableContainer from '@mui/material/TableContainer/TableContainer'
import TableHead from '@mui/material/TableHead/TableHead'
import TableRow from '@mui/material/TableRow/TableRow'
import { roundingNumber } from 'components/shared/processes'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { fetchRations } from 'store/rationSystemSlice'
import { ReactComponent as GroupDeleteIcon } from '../../assets/group_delete_icon.svg'
import { ReactComponent as PlusPopupIcon } from '../../assets/plus_popup_icon.svg'
import { COLORS } from '../../styles/colors'
import { PlanTableCell } from '../../ui/PlanTableCell'
import { TypographyPrimary } from '../../ui/typography/TypographyPrimary'
import { TypographySecondary } from '../../ui/typography/TypographySecondary'
import TimeInput from '../shared/textFields/InputTimeField'

const STICKY_GROUP_WIDTH = 240
const STICKY_RATION_WIDTH = 226
const STICKY_RATION_LEFT = STICKY_GROUP_WIDTH

const stickyGroupCellSx = {
	position: 'sticky',
	left: 0,
	minWidth: STICKY_GROUP_WIDTH,
	width: STICKY_GROUP_WIDTH,
	maxWidth: STICKY_GROUP_WIDTH,
	backgroundColor: 'white',
	zIndex: 21,
}

const stickyGroupHeadCellSx = {
	...stickyGroupCellSx,
	zIndex: 31,
}

const stickyRationCellSx = {
	position: 'sticky',
	left: STICKY_RATION_LEFT,
	minWidth: STICKY_RATION_WIDTH,
	width: STICKY_RATION_WIDTH,
	maxWidth: STICKY_RATION_WIDTH,
	backgroundColor: 'white',
	zIndex: 20,
	boxShadow: '4px 0 8px -4px rgba(0, 0, 0, 0.08)',
}

const stickyRationHeadCellSx = {
	...stickyRationCellSx,
	zIndex: 30,
}

const isValidTimeValue = (time: unknown): boolean =>
	typeof time === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)

const isFilledWeightValue = (weight: unknown): boolean => {
	if (weight === '' || weight == null) return false
	const numericWeight = Number(weight)
	return Number.isFinite(numericWeight) && numericWeight > 0
}

const normalizeWeightValue = (weight: unknown): string | number => {
	if (weight === '' || weight == null) return ''
	const numericWeight = Number(weight)
	return Number.isFinite(numericWeight) ? numericWeight : ''
}

const syncGroupDistributionArrays = <
	T extends {
		time: Array<string>
		weightPercent: Array<string | number>
		weightValue: {
			time: Array<string>
			weightPercent: Array<string | number>
		}
	},
>(
	group: T,
): T => {
	const length = Math.max(
		group.time.length,
		group.weightPercent.length,
		group.weightValue.time.length,
		group.weightValue.weightPercent.length,
	)

	while (group.time.length < length) group.time.push('')
	while (group.weightPercent.length < length) group.weightPercent.push('')
	while (group.weightValue.time.length < length) group.weightValue.time.push('')
	while (group.weightValue.weightPercent.length < length)
		group.weightValue.weightPercent.push('')

	for (let i = 0; i < length; i++) {
		const time = group.time[i] || group.weightValue.time[i] || ''
		const weight = normalizeWeightValue(
			group.weightPercent[i] !== '' && group.weightPercent[i] != null
				? group.weightPercent[i]
				: group.weightValue.weightPercent[i],
		)

		group.time[i] = time
		group.weightValue.time[i] = time
		group.weightPercent[i] = weight
		group.weightValue.weightPercent[i] = weight
	}

	return group
}

const EditPlanTableDistribution = ({
	feedPlanDistribution,
	setAdapterFeedplan,
}) => {
	const dispatch = useAppDispatch()
	const [adapteredArray, setAdapteredArray] = useState([])
	const [focusedField, setFocusedField] = useState<{
		row: number
		col: number
		field: 'time' | 'weight' | 'correction'
	} | null>(null)
	const { rationArray } = useAppSelector(state => state.rationSystemReducer)
	const sxThead = {
		borderBottom: `1px solid ${COLORS.secondaryFontOpacity} !important`,
	}
	const isFocusedField = (
		row: number,
		col: number,
		field: 'time' | 'weight' | 'correction',
	) =>
		focusedField?.row === row &&
		focusedField?.col === col &&
		focusedField?.field === field
	const getInputBorderColor = (
		row: number,
		col: number,
		field: 'time' | 'weight' | 'correction',
		error: boolean,
	) =>
		isFocusedField(row, col, field) ? 'black' : error ? COLORS.error : '#DBDBDB'
	useEffect(() => {
		!rationArray && dispatch(fetchRations())
	}, [])

	useEffect(() => {
		const newArray = []
		feedPlanDistribution.forEach(obj => {
			for (let i = 0; i < obj.groups?.length; i++) {
				const groupName = obj.groups[i].group.title
				const group_id = obj.groups[i].group.id
				const barn = obj.groups[i].group.barn.title
				const cattles_qty = obj.groups[i].group.cattles_qty
				const rawWeight = obj.groups[i].weight_percent
				const weightPercent = [normalizeWeightValue(rawWeight)]
				const correctionPercent = obj.groups[i].correction_percent
				let ration = ''
				if (obj.groups[i].group.ration !== null) {
					ration = obj.groups[i].group.ration.title
				} else {
					ration = 'Рацион не указан'
				}
				const totalWeight =
					obj.groups[i]?.group?.ration_weight_per_head * cattles_qty
				const time = [obj.time ?? '']
				const newObj = {
					groupName,
					group_id,
					correctionPercent,
					barn,
					ration,
					totalWeight,
					time,
					cattles_qty,
					weightPercent,
				}
				newArray.push(newObj)
			}
		})
		const adaptered = newArray.reduce((acc, obj) => {
			const groupIndex = acc.findIndex(item => item.groupName === obj.groupName)
			const incomingTime = obj.time[0]
			const incomingWeight = normalizeWeightValue(obj.weightPercent[0])
			const isCompleteSlot =
				isValidTimeValue(incomingTime) && isFilledWeightValue(incomingWeight)

			if (groupIndex === -1) {
				const time = isCompleteSlot ? [incomingTime] : []
				const weightPercent = isCompleteSlot ? [incomingWeight] : []
				acc.push(
					syncGroupDistributionArrays({
						groupName: obj.groupName,
						group_id: obj.group_id,
						barn: obj.barn,
						ration: obj.ration,
						correctionPercent: obj.correctionPercent,
						totalWeight: obj.totalWeight,
						cattles_qty: obj.cattles_qty,
						time,
						weightPercent,
						weightValue: {
							time: [...time],
							weightPercent: [...weightPercent],
						},
						errorValuesCell: false,
					}),
				)
			} else if (isCompleteSlot) {
				acc[groupIndex].time.push(incomingTime)
				acc[groupIndex].weightPercent.push(incomingWeight)
				acc[groupIndex].weightValue.time.push(incomingTime)
				acc[groupIndex].weightValue.weightPercent.push(incomingWeight)
			}

			if (groupIndex !== -1 || isCompleteSlot) {
				const target = groupIndex === -1 ? acc[acc.length - 1] : acc[groupIndex]
				const sumWeightPercent = target.weightPercent.reduce(
					(sum: number, val: string | number) => sum + (Number(val) || 0),
					0,
				)
				target.errorValuesCell =
					target.weightPercent.length > 0 && sumWeightPercent !== 100
				syncGroupDistributionArrays(target)
			}

			return acc
		}, [])

		const maxSlots = Math.max(
			1,
			...adaptered.map(group => group.time.length || 0),
		)
		adaptered.forEach(group => {
			while (group.time.length < maxSlots) {
				group.time.push('')
				group.weightPercent.push('')
				group.weightValue.time.push('')
				group.weightValue.weightPercent.push('')
			}
			syncGroupDistributionArrays(group)
		})

		setAdapteredArray(adaptered)
	}, [feedPlanDistribution])

	const maxTimeLength = Math.max(
		1,
		...adapteredArray.map(obj => obj.time?.length || 0),
	)
	const weightValueArr = adapteredArray.map(obj => obj.weightValue)

	const getWeightPercentSum = (arr: Array<string | number>): number => {
		let sum = 0
		arr.forEach(value => {
			if (value === '' || value == null) {
				sum += 0
			} else {
				sum += parseFloat(String(value)) || 0
			}
		})
		return sum
	}

	const handleInputValue = (index: number, j: number, value: string) => {
		const normalizedValue = value === '0' || value === '00' ? '' : value
		let nextError = false

		setAdapteredArray(prevData => {
			const newData = prevData.map((item, itemIndex) => {
				if (itemIndex !== index) return item
				const group = syncGroupDistributionArrays({
					...item,
					time: [...item.time],
					weightPercent: [...item.weightPercent],
					weightValue: {
						time: [...item.weightValue.time],
						weightPercent: [...item.weightValue.weightPercent],
					},
				})
				group.weightValue.weightPercent[j] = normalizedValue
				group.weightPercent[j] =
					normalizedValue === '' ? '' : Number(normalizedValue)

				const weightPercentSum = getWeightPercentSum(
					group.weightPercent.map(itemValue =>
						itemValue === '' || itemValue == null ? '' : String(itemValue),
					),
				)
				nextError =
					weightPercentSum !== 100 ||
					group.time[j] === '' ||
					group.weightPercent.some(el => el === '0' || el === 0 || el === '') ||
					normalizedValue === '' ||
					group.time.some(el => el === '__:__' || el === '')

				if (group.weightPercent[j] === '' && group.time[j] === '') {
					nextError = false
				}

				group.errorValuesCell = nextError
				return group
			})
			return newData
		})

		return nextError
	}

	const updateDistributionTime = (
		index: number,
		columnIndex: number,
		newValue: string,
	) => {
		setAdapteredArray(prevData =>
			prevData.map((item, itemIndex) => {
				if (itemIndex !== index) return item
				const group = syncGroupDistributionArrays({
					...item,
					time: [...item.time],
					weightPercent: [...item.weightPercent],
					weightValue: {
						time: [...item.weightValue.time],
						weightPercent: [...item.weightValue.weightPercent],
					},
				})
				group.time[columnIndex] = newValue
				group.weightValue.time[columnIndex] = newValue

				const weightPercentSum = getWeightPercentSum(
					group.weightPercent.map(itemValue =>
						itemValue === '' || itemValue == null ? '' : String(itemValue),
					),
				)
				const hasEmptyPair = group.time.some((timeValue, timeIndex) => {
					const weightValue = group.weightPercent[timeIndex]
					const hasTime =
						timeValue !== '' && timeValue !== '__:__' && timeValue != null
					const hasWeight = isFilledWeightValue(weightValue)
					return (hasTime && !hasWeight) || (!hasTime && hasWeight)
				})

				group.errorValuesCell =
					hasEmptyPair ||
					weightPercentSum !== 100 ||
					group.weightPercent.some(el => el === '0' || el === 0) ||
					(newValue !== '' &&
						!isValidTimeValue(newValue) &&
						newValue !== '__:__')

				return group
			}),
		)
	}

	const addColumn = () => {
		setAdapteredArray(prevData => {
			return prevData.map(obj => {
				const group = syncGroupDistributionArrays({
					...obj,
					time: [...obj.time],
					weightPercent: [...obj.weightPercent],
					weightValue: {
						time: [...obj.weightValue.time],
						weightPercent: [...obj.weightValue.weightPercent],
					},
				})
				group.time.push('')
				group.weightPercent.push('')
				group.weightValue.time.push('')
				group.weightValue.weightPercent.push('')
				return group
			})
		})
	}

	function checkTimeFormat(time) {
		const regex = /^([01]\d|2[0-3]):[0-5]\d$/
		return !regex.test(time)
	}

	const checkWeightSumValues = () => {
		setAdapteredArray(prevData => {
			const newData = [...prevData]
			newData.forEach(obj => {
				let sum = 0
				if (obj.weightPercent.length === 1) {
					sum = obj.weightPercent[0]
					obj.errorValuesCell = sum !== 100
				} else {
					sum = obj.weightPercent.reduce((acc, val) => {
						if (acc === '') {
							acc = 0
						}
						if (val === '') {
							val = 0
						}
						return acc + val
					}, 0)
					obj.errorValuesCell = sum !== 100
				}
			})
			return newData
		})
	}
	const removeColumn = index => {
		setAdapteredArray(prevData => {
			const newData = [...prevData]
			newData.forEach(obj => {
				obj.time.splice(index, 1)
				obj.weightPercent.splice(index, 1)
				obj.weightValue.time.splice(index, 1)
				obj.weightValue.weightPercent.splice(index, 1)
			})
			return newData
		})
		checkWeightSumValues()
	}
	useEffect(() => {
		setAdapterFeedplan(adapteredArray)
	}, [adapteredArray])

	if (!adapteredArray) return <></>
	return (
		<TableContainer
			sx={{
				overflowX: 'auto',
				overflowY: 'hidden',
				'&::-webkit-scrollbar': {
					height: 4,
				},
				'&::-webkit-scrollbar-track': {
					backgroundColor: '#DBDBDB',
					borderRadius: 12,
				},
				'&::-webkit-scrollbar-thumb': {
					backgroundColor: COLORS.icon,
					borderRadius: 12,
				},
			}}
		>
			<Table
				sx={{
					width: '100%',
					[`& .${tableCellClasses.root}`]: {
						whiteSpace: 'nowrap',
						borderBottom: 'none',
					},
				}}
				aria-label='simple table'
			>
				<TableHead sx={sxThead}>
					<TableRow>
						<PlanTableCell
							sx={{
								...stickyGroupHeadCellSx,
								verticalAlign: 'baseline',
							}}
						>
							Группа
						</PlanTableCell>
						<PlanTableCell
							sx={{
								...stickyRationHeadCellSx,
								verticalAlign: 'baseline',
							}}
						>
							Рацион
						</PlanTableCell>
						<PlanTableCell sx={{ verticalAlign: 'baseline' }}>
							<TypographySecondary
								sx={{ width: '210', whiteSpace: 'pre-line' }}
							>
								{'Корректировочный \n процент'}
							</TypographySecondary>
						</PlanTableCell>
						{Array.from({ length: maxTimeLength }, (_, i) => (
							<TableCell
								key={i}
								sx={{ position: 'relative', verticalAlign: 'baseline' }}
							>
								<Box>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
										}}
									>
										<TypographySecondary>Раздача {i + 1}</TypographySecondary>
										{i === 0 ? (
											<></>
										) : (
											<IconButton onClick={() => removeColumn(i)}>
												<GroupDeleteIcon />
											</IconButton>
										)}
									</Box>
									<Box
										sx={{
											position: 'absolute',
											display: ' flex',
											flexDirection: 'row',
											justifyContent: 'space-between',
											top: '70px',
											width: '225px',
										}}
									>
										<TypographySecondary>Время</TypographySecondary>
										<TypographySecondary>Вес раздачи, %</TypographySecondary>
									</Box>
								</Box>
							</TableCell>
						))}
						<TableCell style={{ position: 'sticky', right: 0 }}>
							<PlusPopupIcon
								style={{ cursor: 'pointer' }}
								onClick={addColumn}
							/>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					<TableRow>
						<TableCell sx={{ ...stickyGroupCellSx, padding: '8px' }} />
						<TableCell sx={{ ...stickyRationCellSx, padding: '8px' }} />
					</TableRow>
					{adapteredArray?.map((group, index) => {
						const rowTimes = weightValueArr[index]?.time ?? []
						const rowWeights = weightValueArr[index]?.weightPercent ?? []
						const rowWeightStrings = rowWeights.map(value =>
							value === undefined || value === null ? '' : String(value),
						)
						const rowPairStates = Array.from(
							{ length: maxTimeLength },
							(_, j) => {
								const timeValue = rowTimes[j] ?? ''
								const weightValue = rowWeightStrings[j] ?? ''
								const isPairStarted = timeValue !== '' || weightValue !== ''
								const timeError =
									(isPairStarted &&
										(timeValue === '' || timeValue === '__:__')) ||
									(timeValue !== '' &&
										timeValue !== '__:__' &&
										checkTimeFormat(timeValue))
								const weightError =
									(isPairStarted && weightValue === '') ||
									weightValue === '0' ||
									weightValue === '00'

								return { isPairStarted, timeError, weightError }
							},
						)
						const hasStructuralError = rowPairStates.some(
							pair => pair.timeError || pair.weightError,
						)
						const hasAnyRowValue = rowPairStates.some(
							pair => pair.isPairStarted,
						)
						const hasAnyWeightValue = rowWeightStrings.some(
							weightValue => weightValue !== '',
						)
						const hasSumError =
							!hasStructuralError &&
							hasAnyRowValue &&
							getWeightPercentSum(rowWeightStrings) !== 100
						const rowHasError = hasStructuralError || hasSumError
						const showWeightErrorMessage =
							hasAnyWeightValue &&
							(hasSumError || rowPairStates.some(pair => pair.weightError))

						return (
							<React.Fragment key={`RowElement${index}`}>
								<TableRow sx={{ position: 'relative' }}>
									<TableCell
										component='th'
										scope='row'
										align='left'
										sx={{
											...stickyGroupCellSx,
										}}
									>
										<Box
											display='flex'
											flexDirection='column'
											justifyContent='center'
											sx={{
												borderLeft: `2px solid ${COLORS.icon}`,
												pl: '12px',
												height: 'auto',
											}}
										>
											<Box
												display='flex'
												flexDirection='row'
												alignItems='center'
												sx={{ pb: '12px', width: '210px' }}
											>
												<TypographyPrimary sx={{ whiteSpace: 'wrap' }}>
													{group.groupName}
												</TypographyPrimary>
												<TypographySecondary
													sx={{ ml: '3px', whiteSpace: 'nowrap' }}
												>
													{group.barn}
												</TypographySecondary>
											</Box>
											<TypographyPrimary sx={{ color: COLORS.secondary }}>
												{`Голов: ${group.cattles_qty}`}
											</TypographyPrimary>
										</Box>
									</TableCell>

									<TableCell
										align='left'
										sx={{
											padding: '8px 8px',
											...stickyRationCellSx,
										}}
									>
										<Box sx={{ width: '210px' }}>
											{group.ration === null && group.totalWeight === 0 ? (
												<Box>
													<TypographyPrimary
														sx={{
															whiteSpace: 'nowrap',
															color: COLORS.secondaryFont,
														}}
													>
														Рацион не выбран
													</TypographyPrimary>
												</Box>
											) : (
												<Box>
													<TypographyPrimary
														sx={{ whiteSpace: 'wrap', pb: '12px' }}
													>
														{group.ration === null
															? 'Рацаион не указан'
															: group.ration}
													</TypographyPrimary>
													<TypographyPrimary
														sx={{
															whiteSpace: 'nowrap',
															color: COLORS.secondaryFont,
														}}
													>
														Общий вес:{' '}
														{`${roundingNumber(group.totalWeight, 0)} кг`}
													</TypographyPrimary>
												</Box>
											)}
										</Box>
									</TableCell>
									<TableCell align='left' sx={{ padding: '8px 34px 8px 8px' }}>
										<div
											style={{
												position: 'relative',
												width: '120px',
												height: '50px',
											}}
										>
											<input
												type='text'
												placeholder='%'
												value={group.correctionPercent}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													const newValue = e.currentTarget.value

													if (/^(100|\d{0,3}(\.\d{0,1})?)$/.test(newValue)) {
														setAdapteredArray(prevData => {
															const newData = [...prevData]
															newData[index].correctionPercent = newValue
															return newData
														})
													}
												}}
												onFocus={() => {
													setFocusedField({
														row: index,
														col: -1,
														field: 'correction',
													})
												}}
												onBlur={() => {
													setFocusedField(null)
												}}
												style={{
													width: '120px',
													height: '50px',
													border: '1px solid',
													borderColor: getInputBorderColor(
														index,
														-1,
														'correction',
														false,
													),
													paddingLeft: '5px',
													borderRadius: '12px',
													paddingRight: group.correctionPercent
														? '10px'
														: '5px',
													transition: 'border-color 0.1s ease-in-out',
													textAlign: 'center',
												}}
												readOnly={false}
											/>
											{group.correctionPercent !== 0 &&
												group.correctionPercent !== '' && (
													<span
														style={{
															position: 'absolute',
															top: '68%',
															left: '50%',
															transform: 'translateX(-50%)',
															pointerEvents: 'none',
															color: 'gray',
															textAlign: 'center',
															fontSize: '12px',
															whiteSpace: 'nowrap',
														}}
													>
														{`${roundingNumber(
															(group.totalWeight * group.correctionPercent) /
																100,
															2,
														)} кг`}
													</span>
												)}
											{group.correctionPercent !== '' && (
												<span
													style={{
														position: 'absolute',
														top: '50%',
														left: '50%',
														transform: 'translateY(-50%)',
														marginLeft: `calc(${String(group.correctionPercent).length / 2}ch + 4px)`,
														pointerEvents: 'none',
														color: 'gray',
														fontSize: '0.8rem',
														lineHeight: 1,
													}}
												>
													%
												</span>
											)}
										</div>
									</TableCell>
									{Array(maxTimeLength)
										.fill('')
										.map((_, j) => (
											<TableCell key={`Cell${j}`} sx={{ padding: '8px 8px' }}>
												<Box
													sx={{
														display: 'flex',
														flexDirection: 'row',
														gap: '10px',
													}}
												>
													<TimeInput
														value={weightValueArr[index]?.time?.[j] ?? ''}
														onChange={e => {
															updateDistributionTime(index, j, e.target.value)
														}}
														onFocus={() => {
															setFocusedField({
																row: index,
																col: j,
																field: 'time',
															})
														}}
														onBlur={() => {
															setFocusedField(null)
														}}
														style={{
															width: '120px',
															height: '50px',
															border: '1px solid',
															borderColor: getInputBorderColor(
																index,
																j,
																'time',
																rowPairStates[j]?.timeError ?? false,
															),
															paddingLeft: '5px',
															borderRadius: '12px',
															transition: 'border-color 0.1s ease-in-out',
															textAlign: 'center',
														}}
													/>
													<div
														style={{
															position: 'relative',
															width: '120px',
															height: '50px',
														}}
													>
														<input
															type='text'
															placeholder='Вес, %'
															value={
																weightValueArr[index]?.weightPercent?.[j] ?? ''
															}
															onChange={(
																e: React.ChangeEvent<HTMLInputElement>,
															) => {
																const newValue = e.currentTarget.value

																if (
																	/^(100|\d{0,2}(\.\d{0,2})?)$/.test(newValue)
																) {
																	handleInputValue(index, j, newValue)
																}
															}}
															onFocus={() => {
																setFocusedField({
																	row: index,
																	col: j,
																	field: 'weight',
																})
															}}
															onBlur={() => {
																setFocusedField(null)
															}}
															style={{
																width: '120px',
																height: '50px',
																border: '1px solid',
																borderColor: getInputBorderColor(
																	index,
																	j,
																	'weight',
																	(rowPairStates[j]?.weightError ?? false) ||
																		(hasSumError &&
																			(rowPairStates[j]?.isPairStarted ??
																				false)),
																),
																paddingLeft: '5px',
																borderRadius: '12px',
																paddingRight: weightValueArr[index]
																	.weightPercent[j]
																	? '10px'
																	: '5px',
																transition: 'border-color 0.1s ease-in-out',
																textAlign: 'center',
															}}
															readOnly={false}
														/>
														{weightValueArr[index]?.weightPercent?.[j] !== 0 &&
														weightValueArr[index]?.weightPercent?.[j] !== '' &&
														weightValueArr[index]?.weightPercent?.[j] !==
															undefined ? (
															<span
																style={{
																	position: 'absolute',
																	top: '68%',
																	left: '50%',
																	transform: 'translateX(-50%)',
																	pointerEvents: 'none',
																	color: 'gray',
																	textAlign: 'center',
																	fontSize: '12px',
																	whiteSpace: 'nowrap',
																}}
															>
																{`${roundingNumber(
																	(group.totalWeight *
																		weightValueArr[index].weightPercent[j]) /
																		100,
																	2,
																)} кг`}
															</span>
														) : (
															<></>
														)}

														{weightValueArr[index]?.weightPercent?.[j] !== '' &&
															weightValueArr[index]?.weightPercent?.[j] !==
																undefined && (
																<span
																	style={{
																		position: 'absolute',
																		top: '50%',
																		left: '50%',
																		transform: 'translateY(-50%)',
																		marginLeft: `calc(${String(weightValueArr[index]?.weightPercent?.[j] ?? '').length / 2}ch + 4px)`,
																		pointerEvents: 'none',
																		color: 'gray',
																		fontSize: '0.8rem',
																		lineHeight: 1,
																	}}
																>
																	%
																</span>
															)}
													</div>
												</Box>
											</TableCell>
										))}
								</TableRow>

								{showWeightErrorMessage ? (
									<TableRow sx={{ height: '15px' }}>
										<TableCell
											colSpan={3 + maxTimeLength + 1}
											sx={{ padding: '0px', borderBottom: 'none' }}
										>
											<Box
												sx={{
													position: 'sticky',
													left: 0,
													width: 'fit-content',
													maxWidth: '520px',
													backgroundColor: 'white',
													zIndex: 19,
													padding: '0px 0px 0px 16px',
												}}
											>
												<TypographyPrimary
													sx={{
														fontSize: '12px',
														fontWeight: '400',
														color: `${COLORS.error}`,
														whiteSpace: 'normal',
													}}
												>
													Сумма % веса раздач всех компонентов должна составлять
													100%
												</TypographyPrimary>
											</Box>
										</TableCell>
									</TableRow>
								) : (
									<></>
								)}
							</React.Fragment>
						)
					})}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

export default EditPlanTableDistribution
